// Anzeige-Helfer für Serienmail-Übersicht und -Detail (Handoff E-Mails §8). Zahlen kommen
// ausschließlich aus lib/serienmail/fortschritt (baueBilanz) – hier nur Ton, Text und Balkenanteile.

import type { Ton } from '../ui/ton';
import type { Bilanz, EmpfaengerStatus } from '../../../lib/serienmail/fortschritt';

export type LaufStatus = 'entwurf' | 'geplant' | 'laeuft' | 'fertig' | 'abgebrochen';

export const LAUF_STATUS: Record<LaufStatus, { label: string; ton: Ton }> = {
  entwurf: { label: 'Entwurf', ton: 'neutral' },
  geplant: { label: 'Geplant', ton: 'info' },
  laeuft: { label: 'Läuft', ton: 'warn' },
  fertig: { label: 'Fertig', ton: 'ok' },
  abgebrochen: { label: 'Abgebrochen', ton: 'bad' },
};

export const EMPFAENGER_STATUS: Record<EmpfaengerStatus, { label: string; ton: Ton }> = {
  offen: { label: 'Offen', ton: 'neutral' },
  sending: { label: 'Wird gesendet', ton: 'info' },
  gesendet: { label: 'Gesendet', ton: 'ok' },
  fehler: { label: 'Fehler', ton: 'bad' },
};

export type LaufZielgruppe = 'mieter' | 'eigentuemer' | 'dienstleister' | 'kontakt' | 'gemischt';
export const ZIELGRUPPE_LABEL: Record<LaufZielgruppe, string> = {
  mieter: 'Mieter', eigentuemer: 'Eigentümer', dienstleister: 'Dienstleister', kontakt: 'Sonstige Kontakte', gemischt: 'Mehrere Gruppen',
};
/** #577: „Mieter, Dienstleister“ aus zielgruppen; ältere Läufe ohne Liste nach zielgruppe. */
export function zielgruppenText(zielgruppe: LaufZielgruppe, zielgruppen?: string[] | null): string {
  const liste = (zielgruppen || []).filter((g): g is LaufZielgruppe => g in ZIELGRUPPE_LABEL);
  return liste.length > 0 ? liste.map((g) => ZIELGRUPPE_LABEL[g]).join(', ') : ZIELGRUPPE_LABEL[zielgruppe] ?? zielgruppe;
}

/** Bilanz-Text wie bisher in der Übersicht: „x gesendet · y Fehler · von z“ (Fehler nur, wenn > 0). */
export function bilanzText(b: Pick<Bilanz, 'gesendet' | 'fehler' | 'gesamt'>): string {
  return [`${b.gesendet} gesendet`, b.fehler > 0 ? `${b.fehler} Fehler` : null, `von ${b.gesamt}`].filter(Boolean).join(' · ');
}

/** Balkenanteile in Prozent (gesendet, fehler); leerer Lauf = 0. */
export function balkenAnteile(b: Pick<Bilanz, 'gesendet' | 'fehler' | 'gesamt'>): { gesendet: number; fehler: number } {
  if (b.gesamt <= 0) return { gesendet: 0, fehler: 0 };
  return { gesendet: (b.gesendet / b.gesamt) * 100, fehler: (b.fehler / b.gesamt) * 100 };
}

/** „geplant für 20.10.2026, 08:00 Uhr“ (Entscheidung 10); null ohne gültiges Datum. */
export function geplantFuerText(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const datum = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const zeit = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `geplant für ${datum}, ${zeit} Uhr`;
}

/**
 * Versandtakt nur für die Anzeige (Entscheidung 11). Der echte Takt sitzt fest in der Edge Function
 * (PAKET_GROESSE = 20 je Minute, supabase/functions/serienmail-versand) – beide gehören zusammen.
 */
export const MAILS_PRO_MINUTE = 20;

/** „Versand im Takt von 20 Mails pro Minute · Dauer ca. n Min.“ (mindestens 1 Min.). */
export function taktText(empfaenger: number): string {
  const minuten = Math.max(1, Math.ceil(empfaenger / MAILS_PRO_MINUTE));
  return `Versand im Takt von ${MAILS_PRO_MINUTE} Mails pro Minute · Dauer ca. ${minuten} Min.`;
}
